import { Container, Sprite } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { CoverBackground, sizedVariant, spriteVariant } from "@slot/presentation";
import type { Log, SpineDriver, ViewportStore } from "@slot/presentation";

/**
 * The `spineBackground` prefab: a 4501×2532 box, cover-scaled as a unit, the skeleton's origin at the box's bottom
 * centre (its `content` node at y −1266 y-up), so the art fills the box.
 */
const SPINE_BACKGROUND = { width: 4501, height: 2532, originY: 1266, loop: "animation" } as const;

/**
 * The scene's `BackgroundLayer`: the animated Spine background (`backgrounds/spine/background`) cover-fit to the
 * viewport, falling back to the static (brandable) `backgrounds/background` sprite when the skeleton is missing.
 * `overlayTarget` is what the anticipation's red soft-light ramps.
 */
export class GameBackground extends Container {
  public readonly cover: CoverBackground;
  /** The active variant's root — the anticipation overlay filter goes here. */
  public readonly overlayTarget: Container;
  private readonly spine: Spine | null;

  public constructor(input: { assets: GameVisualAssets; spineDriver: SpineDriver; log: Log }) {
    super({ label: "BackgroundLayer" });
    const skeleton = input.assets.skeletons.background;
    let variant: ReturnType<typeof sizedVariant> | null = null;
    this.spine = null;
    if (skeleton) {
      const box = new Container({ label: "spineBackground" });
      const spine = new Spine({ skeletonData: skeleton, autoUpdate: false });
      spine.label = "content";
      spine.position.set(0, SPINE_BACKGROUND.originY);
      input.spineDriver.attach(spine);
      box.addChild(spine);
      this.spine = spine;
      variant = sizedVariant(box, SPINE_BACKGROUND.width, SPINE_BACKGROUND.height, {
        onActivate: () => {
          if (spine.skeleton.data.findAnimation(SPINE_BACKGROUND.loop) && !spine.state.getCurrent(0)) {
            spine.state.setAnimation(0, SPINE_BACKGROUND.loop, true);
          }
        },
      });
    } else if (input.assets.textures.background) {
      variant = spriteVariant(new Sprite(input.assets.textures.background));
    } else {
      input.log("[GameBackground] no background art loaded");
    }
    // The scene authored three roots (desktop / portrait / landscape) hosting the same prefab with no per-variant
    // offsets, so one shared variant is equivalent.
    this.cover = new CoverBackground({ variants: variant ? { shared: variant } : {} });
    this.addChild(this.cover);
    this.overlayTarget = variant ? variant.target : this.cover;
  }

  public bind(viewport: ViewportStore): void {
    this.cover.bind(viewport);
  }

  public dispose(): void {
    this.cover.dispose();
    if (this.spine && !this.spine.destroyed) this.spine.destroy();
    this.destroy({ children: true });
  }
}
